import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  newTripSchema,
  TRIP_COVER_GRADIENTS,
  TRIP_CURRENCIES,
  type NewTripFormValues,
} from '@/utils/schemas'
import { getTrip, updateTrip, DateRangeConflictError } from '@/services/tripService'

export function EditTripPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { tripId } = useParams<{ tripId: string }>()
  const queryClient = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [conflict, setConflict] = useState<{ dayCount: number; activityCount: number } | null>(null)

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<NewTripFormValues>({
    resolver: zodResolver(newTripSchema),
  })

  useEffect(() => {
    const trip = tripQuery.data
    if (!trip) return
    reset({
      title: trip.title,
      destination: trip.destination ?? '',
      coverUrl: trip.cover_url ?? TRIP_COVER_GRADIENTS[0],
      startDate: trip.start_date,
      endDate: trip.end_date,
      currency: trip.currency as NewTripFormValues['currency'],
    })
  }, [tripQuery.data, reset])

  const coverUrl = watch('coverUrl')
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const dayCount =
    startDate && endDate && endDate >= startDate
      ? Math.round(
          (new Date(`${endDate}T00:00:00Z`).getTime() -
            new Date(`${startDate}T00:00:00Z`).getTime()) /
            86_400_000,
        ) + 1
      : null

  const mutation = useMutation({
    mutationFn: ({
      values,
      confirmUnassign,
    }: {
      values: NewTripFormValues
      confirmUnassign: boolean
    }) =>
      updateTrip(
        tripId!,
        {
          title: values.title,
          destination: values.destination,
          startDate: values.startDate,
          endDate: values.endDate,
          coverUrl: values.coverUrl,
          currency: values.currency,
        },
        confirmUnassign,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['trips'] })
      await queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
      await queryClient.invalidateQueries({ queryKey: ['days', tripId] })
      await queryClient.invalidateQueries({ queryKey: ['activities'] })
      navigate(`/trips/${tripId}/itinerary`)
    },
    onError: (error, variables) => {
      if (error instanceof DateRangeConflictError && !variables.confirmUnassign) {
        setConflict({
          dayCount: error.affectedDayCount,
          activityCount: error.affectedActivityCount,
        })
      }
    },
  })

  const goToStep2 = async () => {
    const valid = await trigger(['title', 'destination'])
    if (valid) setStep(2)
  }

  const onSubmit = (values: NewTripFormValues) => {
    setConflict(null)
    mutation.mutate({ values, confirmUnassign: false })
  }

  const confirmAndSave = (values: NewTripFormValues) => {
    mutation.mutate({ values, confirmUnassign: true })
  }

  if (tripQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('editTrip.loading')}</p>
  }

  return (
    <div className="mx-auto min-h-screen max-w-2xl bg-night px-10 py-12">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-6 font-sans text-sm text-mist"
      >
        {t('itinerary.backToTrips')}
      </button>

      <h1 className="mb-6 font-display text-3xl text-ivory">
        {step === 1 ? t('editTrip.step1Title') : t('editTrip.step2Title')}
      </h1>

      <div className="mb-8 flex gap-2">
        {[1, 2].map((s) => (
          <div
            key={s}
            className={'h-[3px] flex-1 rounded-full ' + (s <= step ? 'bg-brass' : 'bg-panel-2')}
          />
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={step === 1 ? 'flex flex-col gap-4' : 'hidden'}>
          <div>
            <label htmlFor="title" className="mb-1.5 block font-sans text-sm text-ivory">
              {t('newTrip.tripTitle')}
            </label>
            <input
              id="title"
              className="w-full rounded border border-line-strong bg-panel px-3.5 py-3 text-ivory"
              {...register('title')}
            />
            {errors.title && (
              <p role="alert" className="mt-1 text-sm text-brass-warm">
                {t('newTrip.titleRequired')}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="destination" className="mb-1.5 block font-sans text-sm text-ivory">
              {t('newTrip.destination')}
            </label>
            <input
              id="destination"
              className="w-full rounded border border-line-strong bg-panel px-3.5 py-3 text-ivory"
              {...register('destination')}
            />
          </div>

          <div>
            <label htmlFor="currency" className="mb-1.5 block font-sans text-sm text-ivory">
              {t('newTrip.currency')}
            </label>
            <select
              id="currency"
              className="w-full rounded border border-line-strong bg-panel px-3.5 py-3 text-ivory"
              {...register('currency')}
            >
              {TRIP_CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>

          <div>
            <span className="mb-2.5 block font-sans text-sm text-ivory">
              {t('newTrip.coverAmbiance')}
            </span>
            <div className="flex gap-2.5">
              {TRIP_COVER_GRADIENTS.map((gradient) => (
                <button
                  key={gradient}
                  type="button"
                  aria-label={t('newTrip.selectCover')}
                  aria-pressed={coverUrl === gradient}
                  onClick={() => setValue('coverUrl', gradient)}
                  className={
                    'h-14 flex-1 rounded ' +
                    (coverUrl === gradient
                      ? 'border-2 border-brass'
                      : 'border-2 border-transparent')
                  }
                  style={{ background: gradient }}
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={goToStep2}
            className="mt-2 rounded border border-brass bg-brass px-5 py-2.5 font-sans text-sm font-medium text-night"
          >
            {t('newTrip.continue')}
          </button>
        </div>

        <div className={step === 2 ? 'flex flex-col gap-4' : 'hidden'}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="mb-1.5 block font-sans text-sm text-ivory">
                {t('newTrip.startDate')}
              </label>
              <input
                id="startDate"
                type="date"
                className="w-full rounded border border-line-strong bg-panel px-3.5 py-3 text-ivory"
                {...register('startDate')}
              />
            </div>
            <div>
              <label htmlFor="endDate" className="mb-1.5 block font-sans text-sm text-ivory">
                {t('newTrip.endDate')}
              </label>
              <input
                id="endDate"
                type="date"
                className="w-full rounded border border-line-strong bg-panel px-3.5 py-3 text-ivory"
                {...register('endDate')}
              />
              {errors.endDate && (
                <p role="alert" className="mt-1 text-sm text-brass-warm">
                  {errors.endDate.message}
                </p>
              )}
            </div>
          </div>

          {dayCount !== null && (
            <div className="rounded-r border-l-[3px] border-line border-l-brass bg-panel px-4.5 py-3.5">
              <div className="text-sm text-ivory">{t('newTrip.dayCount', { count: dayCount })}</div>
            </div>
          )}

          {conflict && (
            <div className="rounded-r border-l-[3px] border-line border-l-brass-warm bg-panel px-4.5 py-3.5">
              <div className="text-sm text-ivory">
                {t('editTrip.conflictWarning', {
                  count: conflict.dayCount,
                  dayCount: conflict.dayCount,
                  activityCount: conflict.activityCount,
                })}
              </div>
              <button
                type="button"
                onClick={handleSubmit(confirmAndSave)}
                disabled={mutation.isPending}
                className="mt-3 rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
              >
                {t('editTrip.confirmAndSave')}
              </button>
            </div>
          )}

          {mutation.isError && !conflict && (
            <p role="alert" className="text-sm text-brass-warm">
              {t('editTrip.error')}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="rounded border border-line-strong px-5 py-2.5 font-sans text-sm text-ivory"
            >
              {t('newTrip.back')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded border border-brass bg-brass px-5 py-2.5 font-sans text-sm font-medium text-night disabled:opacity-50"
            >
              {t('editTrip.save')}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
